/** The standard quick actions for a tag chip on a detail surface — pass as
 *  `TagBadgeSet chipActions={(t) => tagChipActions(t, ctx)}`.
 *
 *  Open the tag · filter the surface's grid by this exact tag (id-pinned, so
 *  same-named tags in other categories do not leak in) · open / filter by its
 *  category · copy. The grid and relation come from the context: a booking's
 *  own tags filter /bookings by `tag=`, an order's tags filter /orders by
 *  `order_tag=` (and /bookings by `order_tag=`), a tour's tags filter /tours
 *  by `tag=` and /bookings by `tour_tag=`, a user's tags filter the users grid
 *  by `?tag=` and /bookings by `user_tag=`.
 */
import { act, type ValueAction } from '@/components/ValueActions/ValueActions';
import {
  bookingsHref,
  cond,
  idCond,
  ordersHref,
  toursHref,
  usersHref,
  type UserKind,
} from '@/utils/gridLinks';

import type { TagBadge } from './TagBadgeSet';

export interface TagChipContext {
  /** Scopes the grid links to one operator (`?tenant=`). */
  tenantId?: number | null | undefined;
  /** Whose tags these are — decides the grids and relations offered. */
  owner?: 'booking' | 'order' | 'tour' | 'user';
  /** For `owner: 'user'`: which users grid. Default guests. */
  userKind?: UserKind;
}

/** The category namespaces paired with each tag relation on /bookings and
 *  /orders (`tag` ↔ `category`, `order_tag` ↔ `order_category`, …). */
const CATEGORY_NS: Record<string, string> = {
  tag: 'category',
  order_tag: 'order_category',
  user_tag: 'user_category',
  tour_tag: 'tour_category',
};

export function tagChipActions(tag: TagBadge, ctx: TagChipContext = {}): ValueAction[] {
  const { tenantId, owner = 'booking', userKind = 'guests' } = ctx;
  const out: ValueAction[] = [act.open(`/tags/${tag.id}`, 'Open tag')];

  const filterBy = (label: string, href: string) => out.push(act.filter(href, label));
  const bookingsBy = (relation: string, label: string) =>
    filterBy(label, bookingsHref([idCond(relation, tag.id)], tenantId));
  const categoryOn = (
    grid: 'bookings' | 'orders',
    relation: string,
    code: string,
    label: string,
  ) => {
    const ns = CATEGORY_NS[relation];
    if (!ns) return;
    const href = grid === 'bookings'
      ? bookingsHref([cond(ns, code)], tenantId)
      : ordersHref([cond(ns, code)], tenantId);
    filterBy(label, href);
  };

  switch (owner) {
    case 'booking':
      bookingsBy('tag', 'Bookings with this tag');
      break;
    case 'order':
      filterBy('Orders with this tag', ordersHref([idCond('order_tag', tag.id)], tenantId));
      bookingsBy('order_tag', 'Bookings whose order has this tag');
      break;
    case 'tour':
      filterBy('Tours with this tag', toursHref([idCond('tag', tag.id)], tenantId));
      bookingsBy('tour_tag', 'Bookings on tours with this tag');
      break;
    case 'user':
      filterBy(
        'Users with this tag',
        usersHref(userKind, { tenant: tenantId ?? undefined, tag: tag.id }),
      );
      bookingsBy('user_tag', 'Bookings of users with this tag');
      break;
  }

  if (tag.category) {
    if (tag.category.id !== undefined) {
      out.push(act.open(`/tag-categories/${tag.category.id}`, 'Open category'));
    }
    const code = tag.category.code;
    switch (owner) {
      case 'booking':
        categoryOn('bookings', 'tag', code, 'Bookings with any tag in this category');
        break;
      case 'order':
        categoryOn('orders', 'order_tag', code, 'Orders with any tag in this category');
        break;
      case 'tour':
        categoryOn('bookings', 'tour_tag', code, 'Bookings on tours with any tag in this category');
        break;
      case 'user':
        categoryOn('bookings', 'user_tag', code, 'Bookings of users with any tag in this category');
        break;
    }
  }

  out.push(act.copy(tag.name, 'Copy name'), act.copy(String(tag.id), 'Copy id'));
  return out;
}
